import React from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {Col, Row, Table, Input} from 'reactstrap';
import {RootState} from '../../app/rootReducer';
import {RouteComponentProps} from 'react-router';
import {fetchUsers, createOrUpdateUser} from './userManagementSlice';
import {User} from '../../interfaces/models';

import styles from './UserManagementPage.module.css';
import 'font-awesome/css/font-awesome.css';

type Props = ConnectedProps<typeof connector>;

interface State {
    editIdx: number,
    editMode: boolean,
    editUser: User
}

const initialState = (): State => ({
    editIdx: -1, 
    editMode: false,
    editUser: new User()
});

class UserManagementPage extends React.Component<Props, State> {
    readonly state: State = initialState();
    
    async componentDidMount() {
        const {users, fetchUsers} = this.props;
        if (!users?.length) {
            await fetchUsers();
        }
    }
    
    handleEdit(idx: number) {
        const {users} = this.props;
        const user = idx < users.length && idx >= 0 ? users[idx] : new User();
        this.setState({
            editIdx: idx,
            editMode: true,
            editUser: user
        });
    }
    
    async handleSave() {
        if (this.state.editIdx === -1 && this.props.users.find(u => u.email === this.state.editUser.email)) {
            return;
        }
        const {createOrUpdateUser} = this.props;
        await createOrUpdateUser(this.state.editUser);
        this.setState(initialState());
    }
    
    handleCancelEdit() {
        this.setState(initialState());
    }
    
    handleNew() {
        this.setState(initialState());
        this.setState({editMode: true});
    }
    
    render() {
        const {editIdx, editMode, editUser} = this.state;
        
        const renderUserTable = () => {
            return <Table>
                {renderTableHeader()}
                {renderTableContent()}
            </Table>
        }

        const renderTableHeader = () => {
            return <thead>
            <tr>
                <th style={{width: '25%'}}>Email</th>
                <th style={{width: '25%'}}>Password</th>
                <th style={{width: '15%'}}>Is Admin</th>
                <th style={{width: '15%'}}>Is Enabled</th>
                <th style={{width: '20%'}}>Actions</th>
            </tr>
            </thead>;
        }
        
        const renderTableContent = () => {
            const {users} = this.props;
            return <tbody>
                {!!users?.length && users.map((x, i) => renderTableRow(x, i))}
                {(!editMode || editIdx === -1) && renderNewUserRow()}
            </tbody>;
        };
        
        const renderNewUserRow = () => {
            const renderNewButton = () => {
                return <span onClick={() => this.handleNew()} className={styles.actionButton}>
                    <i className="fa fa-plus" aria-hidden="true"/> New user
                </span>;
            };
            
            if (editMode && editIdx === -1) 
                return renderUserEdit(new User(), -1);
            
            return <tr>
                <td/>
                <td/>
                <td/>
                <td/>
                <td>{renderNewButton()}</td>
            </tr>;
        };
        
        const renderTableRow = (user: User, key: number) => {
            return editMode && editIdx === key ? renderUserEdit(user, key) : renderUserView(user, key);
        };
        
        const renderUserView = (user: User, key: number) => {
            const renderEditButton = () => {
                if (editMode || user.email === this.props.username) return null;
                return <span onClick={() => this.handleEdit(key)} className={styles.actionButton}>
                    <i className="fa fa-edit" aria-hidden="true"/> Edit
                </span>;
            };
            
            return <tr key={key}>
                <td>{user.email}</td>
                <td>***</td>
                <td>{user.isAdmin ? 'Yes' : 'No'}</td>
                <td>{user.isEnabled ? 'Yes' : 'No'}</td>
                <td>{renderEditButton()}</td>
            </tr>;
        };

        const renderUserEdit = (user: User, key: number) => {
            const renderSaveButton = () => {
                return <span onClick={() => this.handleSave()} className={styles.actionButton}>
                    <i className="fa fa-save" aria-hidden="true"/> Save
                </span>;
            };

            const renderCancelButton = () => {
                return <span onClick={() => this.handleCancelEdit()} className={styles.actionButton}>
                    <i className="fa fa-undo" aria-hidden="true"/> Cancel
                </span>;
            };
            
            return <tr key={key}>
                <td className={styles.inputColActive}>
                    <Input type="email" name="email" value={editUser.email} disabled={key < this.props.users.length && key >= 0}
                           onChange={(e) => this.setState({editUser: {...editUser, email: e.target.value}})} />
                </td>
                <td className={styles.inputColActive}>
                    <Input type="password" name="password" value={editUser.password || ''}
                           onChange={(e) => this.setState({editUser: {...editUser, password: e.target.value}})} />
                </td>
                <td>
                    <Input type="checkbox" name="isAdmin" className={styles.checkBox} checked={editUser.isAdmin} 
                           onChange={() => this.setState({editUser: {...editUser, isAdmin: !editUser.isAdmin}})} />
                </td>
                <td>
                    <Input type="checkbox" name="isEnabled" className={styles.checkBox} checked={editUser.isEnabled} 
                           onChange={() => this.setState({editUser: {...editUser, isEnabled: !editUser.isEnabled}})} />
                </td>
                <td>{renderSaveButton()}<div style={{display: 'inline-block', width: '10pt'}} />{renderCancelButton()}</td>
            </tr>;
        };
        
        return <>
            <Row>
                <Col md={12} className="pb-3">
                    <h2>Manage users</h2>
                </Col>
            </Row>
            <Row className="pb-5">
                <Col md={12}>
                    {renderUserTable()}
                </Col>
            </Row>
        </>;
    }
}

const mapStateToProps = (state: RootState, ownProps: RouteComponentProps) => {
    return {
        ...state.userManagement,
        ...ownProps,
        username: state.oidc.user?.profile?.name,
        isFetching: state.api.loadingProcList.indexOf('fetchUsers') > -1 
            || state.api.loadingProcList.indexOf('createOrUpdateUser') > -1
    };
};

const mapDispatchToProps = {fetchUsers, createOrUpdateUser};
const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(UserManagementPage);

